/**
 * useTaxonomy Hook
 *
 * Fetches and caches category taxonomy + tags for use across components.
 * Implements caching strategy with localStorage for performance.
 *
 * Features:
 * - Loads taxonomy on mount
 * - Caches in localStorage (24 hour TTL)
 * - Provides loading and error states
 * - Automatic cache invalidation
 *
 * Usage:
 * const { taxonomy, loading, error, refetch } = useTaxonomy();
 *
 * Location: apps/web/src/hooks/useTaxonomy.js
 * Part of: MVP 1.3 Phase 2 - Search Taxonomy & SEO
 */

import { useState, useEffect } from 'react';
import { getCompleteTaxonomy } from '../services/taxonomyService';

const CACHE_KEY = 'taxonomy_cache';
const CACHE_TTL = 24 * 60 * 60 * 1000; // 24 hours in milliseconds

/**
 * Get cached taxonomy from localStorage
 * @returns {Object|null} Cached taxonomy or null if expired/missing
 */
function getCachedTaxonomy() {
  try {
    const cached = localStorage.getItem(CACHE_KEY);
    if (!cached) {return null;}

    const { data, timestamp } = JSON.parse(cached);
    const now = Date.now();

    // Check if cache is expired
    if (now - timestamp > CACHE_TTL) {
      localStorage.removeItem(CACHE_KEY);
      return null;
    }

    return data;
  } catch (error) {
    console.error('[useTaxonomy] Error reading cache:', error);
    return null;
  }
}

/**
 * Set taxonomy cache in localStorage
 * @param {Object} data - Taxonomy data to cache
 */
function setCachedTaxonomy(data) {
  try {
    const cacheEntry = {
      data,
      timestamp: Date.now()
    };
    localStorage.setItem(CACHE_KEY, JSON.stringify(cacheEntry));
  } catch (error) {
    console.error('[useTaxonomy] Error writing cache:', error);
  }
}

/**
 * useTaxonomy Hook
 * @returns {Object} { taxonomy, loading, error, refetch }
 */
export default function useTaxonomy() {
  const [taxonomy, setTaxonomy] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTaxonomy = async () => {
    try {
      setLoading(true);
      setError(null);

      // Try cache first
      const cached = getCachedTaxonomy();
      if (cached) {
        setTaxonomy(cached);
        setLoading(false);
        return;
      }

      // Fetch from API
      const data = await getCompleteTaxonomy({ includeCounts: false });

      // Store in cache
      setCachedTaxonomy(data);

      // Update state
      setTaxonomy(data);
      setLoading(false);
    } catch (err) {
      console.error('[useTaxonomy] Error fetching taxonomy:', err);
      setError(err);
      setLoading(false);
    }
  };

  // Fetch on mount
  useEffect(() => {
    fetchTaxonomy();
  }, []);

  return {
    taxonomy,
    loading,
    error,
    refetch: fetchTaxonomy
  };
}
